/**
 * Runtime helper for resolving local MFE configuration.
 * Imports the JSON config and exposes typed lookup functions
 * for use in the browser bundle.
 *
 * On mobile (Capacitor), URLs route through the webpack dev server proxy
 * so the device/emulator can reach the host machine's localhost MFE servers.
 */

import localMfesConfig from '../../../../config/local-mfes.json'
import { platform } from '../../services/device/platform'

interface LocalMfeEntry {
  name: string
  port: number
  scope?: string
}

interface ResolvedLocalMfe {
  name: string
  port: number
  scope: string
  url: string
}

function capitalizeFirstLetter (str: string): string {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function getLocalMfeUrl (port: number): string {
  if (platform === 'web') {
    return `http://localhost:${port}/remoteEntry.js`
  }
  // Mobile: route through the dev server proxy to avoid connection issues
  // The emulator/device can't reach the host's localhost directly
  const devHost = process.env.LOCAL_IP || 'localhost'
  return `http://${devHost}:8080/mfe-proxy/${port}/remoteEntry.js`
}

const mfes: LocalMfeEntry[] = localMfesConfig?.mfes ?? []

/**
 * Get local config for a specific MFE by name (case-insensitive).
 * Returns resolved config with URL, or null if not configured locally.
 */
export function getLocalMfeConfig (mfeName: string): ResolvedLocalMfe | null {
  if (!mfeName) return null
  const entry = mfes.find(m => m.name.toLowerCase() === mfeName.toLowerCase())
  if (!entry) return null
  const scope = entry.scope || capitalizeFirstLetter(entry.name)
  return {
    name: entry.name,
    port: entry.port,
    scope,
    url: getLocalMfeUrl(entry.port)
  }
}

/**
 * Get all locally configured MFEs.
 */
export function getAllLocalMfes (): ResolvedLocalMfe[] {
  return mfes.map(entry => {
    const scope = entry.scope || capitalizeFirstLetter(entry.name)
    return {
      name: entry.name,
      port: entry.port,
      scope,
      url: getLocalMfeUrl(entry.port)
    }
  })
}

/**
 * Check if a MFE is configured for local development.
 */
export function isLocalMfe (mfeName: string): boolean {
  return getLocalMfeConfig(mfeName) !== null
}
